import { useState } from "react"

import { Form } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { InformationAlert } from "@/molecules/InformationAlert"
import { CreateTicketButton } from "@/organisms/CreateTicketButton"
import { TicketTemplateModal } from "@/organisms/Ticketing/components/TicketTemplateModal"
import { useTicketing } from "@/organisms/Ticketing/hooks/useTicketing"
import {
  useTemplateActionTest,
  useTicketIssueExtra
} from "@/pages/GovernancePage/applications/workflow/useWorkflow"
import {
  getTemplateButtonT,
  getWritePermission,
  TICKET_ACTION_SYSTEMS,
  TICKET_ACTION_VALUE_KEYS
} from "@/pages/GovernancePage/helpers/workflows"

import styles from "../ActionsSectionForm.module.scss"

import type { TicketIssueActionPayload } from "@/api/useWorkflow.types"
import type { TicketIssueActionFormProps } from "./TicketIssueAction.types"

export const TicketIssueActionForm = (props: TicketIssueActionFormProps) => {
  const { actionType, triggerType, path, form, t } = props
  const system = TICKET_ACTION_SYSTEMS[actionType]
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "settings.automatedActions.form"
  })
  const { t: tSystems } = useTranslation("translation", {
    keyPrefix: "ticketing.systems"
  })
  const vendor = { vendor: tSystems(system) }
  const fieldPath = ["actions", path, "ticketIssue"]
  const errorPath = ["actions", path, "relatedError"]
  const template = Form.useWatch<TicketIssueActionPayload | undefined>(
    fieldPath,
    form
  )
  // No field renders the error, so its value has to be preserved to be watched
  const relatedError = Form.useWatch<Jira.IssueError | undefined>(errorPath, {
    form,
    preserve: true
  })
  const { systems } = useTicketing()
  const [open, setOpen] = useState(false)
  const { response: extra, isLoading } = useTicketIssueExtra({
    action_type: actionType,
    trigger_type: triggerType
  })
  const [{ testTemplate }, { isPending: isTestLoading }] =
    useTemplateActionTest()
  const { hasPermission } = useUser()
  const hasWritePermissions = !!hasPermission(getWritePermission())

  const onTest = (payload: TicketIssueActionPayload) =>
    testTemplate(
      { [TICKET_ACTION_VALUE_KEYS[actionType]]: payload },
      actionType,
      triggerType
    )

  const closeModal = (payload?: TicketIssueActionPayload) => {
    if (payload) {
      form.setFieldValue(fieldPath, payload)
      form.setFieldValue(errorPath, undefined)
      form.validateFields([fieldPath])
    }

    setOpen(false)
  }

  return (
    <div className={styles.actionExtraFields}>
      {!template && (
        <InformationAlert
          type="warning"
          message={tForm("alerts.jiraIssueRequired")}
        />
      )}
      {template && relatedError && (
        <InformationAlert
          type="error"
          message={tForm("alerts.ticketIssueConfigurationError", vendor)}
        />
      )}
      <Form.Item
        name={[path, "ticketIssue"]}
        rules={[{ required: true, message: t("error.requiredField") }]}
      >
        <CreateTicketButton
          hasIntegration={systems.includes(system)}
          systems={[system]}
          t={getTemplateButtonT({
            t,
            buttonKey: `ticketing.createButton.${system}`,
            isNew: !template,
            createLabel: tForm(
              "fields.selectActionPlaceholder.createTicketTemplate",
              vendor
            ),
            editLabel: tForm(
              "fields.selectActionPlaceholder.editTicketTemplate",
              vendor
            )
          })}
          onClick={() => setOpen(true)}
          outlined
          typeBtn="secondary"
          sizeBtn="medium"
          disabled={isLoading}
          className={styles.jiraButton}
          below={
            <div className={styles.jiraNote}>
              {tForm("fields.selectActionPlaceholder.ticketNote", vendor)}
            </div>
          }
        />
      </Form.Item>
      <TicketTemplateModal
        key={open ? "open" : "closed"}
        system={system}
        open={open}
        existingTemplate={template}
        validationErrors={relatedError}
        extra={extra ?? undefined}
        closeModal={closeModal}
        testConfig={{ onTest, isLoading: isTestLoading }}
        hasWritePermissions={hasWritePermissions}
      />
    </div>
  )
}
